import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { MatSort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import {
  OperationStatus,
  HpcJobStageNames,
  CloudTypes,
  HpcVMStates,
  JobTypes,
} from "src/app/core/models/globals";
import {
  Job,
  JobAction,
  JobActionsText,
  JobActionsValue,
  JobGlobalState,
  OutputDownloadStatus,
} from "src/app/core/models/job";
import { UserRoleNames } from "src/app/core/models/users";
import { AuthenticationService } from "src/app/core/services/authentication.service";
import { DeploymentService } from "src/app/core/services/deployment.service";
import { EnvironmentService } from "src/app/core/services/environment.service";
import { JobsService } from "src/app/core/services/jobs.service";
import { StorageService } from "src/app/core/services/storage.service";

const DataBackUpOptionNotSelected = "Data back up option not selected";

@Component({
  selector: "app-job-advance-view",
  templateUrl: "./job-advance-view.component.html",
  styleUrl: "./job-advance-view.component.scss",
})
export class JobAdvanceViewComponent {
  private jobService = inject(JobsService);

  selectedJob: Job = this.jobService.activeJob;
  @Output() hideDetails = new EventEmitter();
  @Output() maximize = new EventEmitter();
  @ViewChild(MatSort) sort: MatSort;
  @Input() CurrentCustomAction: JobAction[];

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private authenticationService = inject(AuthenticationService);
  private env = inject(EnvironmentService);
  private deploymentService = inject(DeploymentService);

  loggedUser = this.authenticationService.currentUserValue.username;
  currentStorageName: string = this.storageService.getActiveStorageName();
  allowAdminJobActionsToUsers = this.env.envVariables.allowAdminJobActions;
  cloudType: string =
    this.route.snapshot.params["cloudType"] ||
    this.route.parent.snapshot.params["cloudType"];
  syncStoreUsage: null;
  workStoreUsage: null;
  jobSyncDir: any;
  toShow: boolean;
  jobWorkDir: any;
  jobs: any;
  jobNodeName: string;
  active = 0;
  jobActions = [];
  autoRescaleFlag: boolean;
  jobGlobalStateDelete: string = JobGlobalState.Delete.toString();
  isMaximized = false;

  constructor() { }

  ngOnInit(): void {
    this.jobService.jobData.subscribe((data) => {
      this.selectedJob = this.jobService.activeJob;
      if (this.active == 1) {
        this.openJobDetails();
      } else {
        this.handleActiveTab();
      }
    });
  }

  maximizeAdvancedView(currentValue) {
    if (currentValue) {
      this.maximize.emit(80);
      this.isMaximized = false;
    } else {
      this.maximize.emit(100);
      this.isMaximized = true;
    }
  }

  close() {
    this.maximize.emit(80);
    this.hideDetails.emit("true");
  }

  handleActiveTab() {
    if (this.selectedJob?.state == JobGlobalState.Inactive && this.selectedJob.outputDirectory.startsWith('/')) {
      this.openJobSyncstore();
    }
    else if (this.selectedJob?.state == JobGlobalState.Delete) {
      this.openJobDetails();
    }
    else {
      this.openJobWorkstore();
    }
  }

  tabChanged(event) { }

  openJobDetails() {
    this.active = 1;
    this.router.navigate([this.selectedJob.name, "jobDetails"], {
      relativeTo: this.route,
    });
    this.syncStoreUsage = null;
    this.workStoreUsage = null;
  }

  openJobSyncstore() {
    this.active = 2;
    this.jobNodeName = this.selectedJob?.nodelist?.split(",")[0];
    this.jobService.sendJobNode(this.jobNodeName);
    this.jobSyncDir =
      this.selectedJob?.outputDirectory == DataBackUpOptionNotSelected
        ? `${this.env.envVariables.syncStoreRoute}/${this.loggedUser}`
        : this.selectedJob?.outputDirectory;
    this.jobSyncDir = this.jobSyncDir
      .replace("D:", "/mnt/syncstore")
      .replace(/\\/g, "/");

    this.storageService.sendJobStorageDirectoryPath(this.jobSyncDir);
    this.router.navigate(
      [this.selectedJob.name, "storage", this.selectedJob.assignedStorage],
      { relativeTo: this.route }
    );
    this.toShow = true;
    // this.getSyncStoreUsageData();
    this.workStoreUsage = null;
  }

  openJobWorkstore() {
    this.active = 3;
    this.jobService.activeJob = this.selectedJob;
    this.jobNodeName = this.selectedJob?.nodelist?.split(",")[0];
    this.jobService.sendJobNode(this.jobNodeName);
    // if (!this.selectedJob?.isLocalStore) {
    //   this.jobService.sendJobNode("");
    // }
    this.jobWorkDir = this.selectedJob.workDirectory;
    if (this.jobWorkDir == "" || this.jobWorkDir == null) {
      this.jobWorkDir = `${this.env.envVariables.workStoreRoute}/${this.loggedUser}/${this.selectedJob.name}`;
    }
    this.storageService.sendJobStorageDirectoryPath(this.jobWorkDir);
    this.router.navigate(
      [this.selectedJob.name, "storage", this.currentStorageName],
      { relativeTo: this.route }
    );
    this.toShow = true;
    this.syncStoreUsage = null;
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case "inactive":
        return "status-inactive";
      case "active":
        return "status-active";
      case "succeeded":
        return "status-succeeded";
      case "failed":
        return "status-failed";
      default:
        return "status-running";
    }
  }

  get getJobActionsForThisJob(): JobAction[] {
    let jobActions: JobAction[] = [];

    if (this.selectedJob) {
      let role = this.authenticationService.getRole();
      if (
        this.loggedUser == this.selectedJob.user ||
        (role == UserRoleNames.GlobalAdmin && this.allowAdminJobActionsToUsers)
      ) {
        if (
          this.selectedJob.state == JobGlobalState.Active &&
          this.selectedJob.status != OperationStatus.Cancelled &&
          this.selectedJob.status != OperationStatus.Cancelling &&
          this.selectedJob.status != OperationStatus.ContactSupport &&
          this.selectedJob.status != OperationStatus.Wait
        ) {
          jobActions.push(
            new JobAction(JobActionsText.TxtCancel, JobActionsValue.ValCancel)
          );
          jobActions.push(
            new JobAction(
              JobActionsText.TxtRunCommand,
              JobActionsValue.ValRunCommand
            )
          );
          if (this.selectedJob.status == OperationStatus.Queued) {
            if (this.selectedJob.isHeld) {
              jobActions.push(
                new JobAction(
                  JobActionsText.TxtReleaseJob,
                  JobActionsValue.ValReleaseJob
                )
              );
            } else {
              jobActions.push(
                new JobAction(
                  JobActionsText.TxtHoldJob,
                  JobActionsValue.ValHoldJob
                )
              );
            }
          }

          if (
            this.selectedJob.stage != HpcJobStageNames.STAGE_JOB_EXECUTE ||
            this.selectedJob.status != OperationStatus.Running
          ) {
            jobActions.push(
              new JobAction(
                JobActionsText.TxtChangeJobPriority,
                JobActionsValue.ValChangeJobPriority
              )
            );
            jobActions.push(new JobAction(JobActionsText.TxtChangeJobPartition, JobActionsValue.ValChangeJobPartition));
          }

          if (this.cloudType == CloudTypes.MicrosoftAzure) {
            if (!this.autoRescaleFlag) {
              this.autoRescaleFlag =
                this.deploymentService.getRescaleFlagOfDeployment(
                  this.selectedJob.deployment
                );
            }
            if (
              this.autoRescaleFlag === false &&
              (role == UserRoleNames.GlobalAdmin ||
                role == UserRoleNames.DeploymentAdmin)
            ) {
              if (
                this.selectedJob.autoShut &&
                this.selectedJob.autoShut.toLowerCase() == "true"
              ) {
                jobActions.push(
                  new JobAction(
                    JobActionsText.TxtDisableAutoShut,
                    JobActionsValue.ValDisableAutoShut
                  )
                );
              } else {
                jobActions.push(
                  new JobAction(
                    JobActionsText.TxtEnableAutoShut,
                    JobActionsValue.ValEnableAutoShut
                  )
                );
              }
            }
          }

          if (
            this.cloudType != CloudTypes.OnPremise &&
            (role == UserRoleNames.GlobalAdmin ||
              role == UserRoleNames.DeploymentAdmin) &&
            this.autoRescaleFlag === false
          ) {
            if (
              this.selectedJob.stopAllocated &&
              this.selectedJob.stopAllocated.toLowerCase() == "true"
            ) {
              jobActions.push(
                new JobAction(
                  JobActionsText.TxtDisableStopAllocatedNodes,
                  JobActionsValue.ValDisableStopAllocatedNodes
                )
              );
            } else {
              jobActions.push(
                new JobAction(
                  JobActionsText.TxtStopAllocatedNodes,
                  JobActionsValue.ValStopAllocatedNodes
                )
              );
            }
          }
        }

        if (this.selectedJob.status == OperationStatus.Wait) {
          jobActions.push(
            new JobAction(JobActionsText.TxtCancel, JobActionsValue.ValCancel)
          );
        }

        if (
          this.selectedJob.state == "Inactive" &&
          this.selectedJob.stage == HpcJobStageNames.STAGE_JOB_EXECUTE &&
          this.selectedJob.status != OperationStatus.Cancelling
        ) {
          jobActions.push(
            new JobAction(
              JobActionsText.TxtResubmit,
              JobActionsValue.ValResubmit
            )
          );
          jobActions.push(
            new JobAction(
              JobActionsText.TxtDataDelete,
              JobActionsValue.ValDataDelete
            )
          );
        }
        if (
          this.selectedJob.state != HpcVMStates.STATE_CODE_STARTED &&
          this.selectedJob.status != OperationStatus.Cancelling
        ) {
          jobActions.push(
            new JobAction(JobActionsText.TxtDelete, JobActionsValue.ValDelete)
          );
        }

        if (
          ["Cancelled", "Cancelling"].includes(this.selectedJob.status) &&
          this.selectedJob.state != "Inactive" &&
          role == UserRoleNames.GlobalAdmin
        ) {
          jobActions.push(
            new JobAction(
              JobActionsText.TxtInactiveJob,
              JobActionsValue.ValInactiveJob
            )
          );
        }

        if (
          this.selectedJob.stage == HpcJobStageNames.STAGE_JOB_EXECUTE &&
          this.selectedJob.application == JobTypes.Abaqus
        ) {
          if (this.selectedJob.isSuspended) {
            jobActions.push(
              new JobAction(
                JobActionsText.TxtResumeJob,
                JobActionsValue.ValResumeJob
              )
            );
          } else if (
            this.selectedJob.status.includes(OperationStatus.Running)
          ) {
            jobActions.push(
              new JobAction(
                JobActionsText.TxtSuspendJob,
                JobActionsValue.ValSuspendJob
              )
            );
          }
        }

        if (
          this.selectedJob.stage != HpcJobStageNames.STAGE_JOB_INIT &&
          this.selectedJob.status != OperationStatus.Cancelled
        ) {
          if (
            this.selectedJob.stage == HpcJobStageNames.STAGE_JOB_EXECUTE &&
            (this.selectedJob.status == OperationStatus.Succeeded ||
              this.selectedJob.status == OperationStatus.Failed) &&
            this.selectedJob.downloadState == OutputDownloadStatus.Disabled
          ) {
            jobActions.push(
              new JobAction(
                JobActionsText.TxtEnableDownload,
                JobActionsValue.ValEnableDownload
              )
            );
          }

          if (
            this.selectedJob.downloadState &&
            [
              OutputDownloadStatus.Completed,
              OutputDownloadStatus.Failed,
              OutputDownloadStatus.Cancelled,
              OutputDownloadStatus.Unknown,
              "Unknown",
            ].includes(this.selectedJob.downloadState)
          ) {
            jobActions.push(
              new JobAction(
                JobActionsText.TxtReEnableDownload,
                JobActionsValue.ValReEnableDownload
              )
            );
          }

          if (
            (this.selectedJob.downloadState &&
              this.selectedJob.downloadState == OutputDownloadStatus.Enabled) ||
            this.selectedJob.downloadState == OutputDownloadStatus.InProgress
          ) {
            jobActions.push(
              new JobAction(
                JobActionsText.TxtCancelDownload,
                JobActionsValue.ValCancelDownload
              )
            );
          }
        }
      }

      if (
        this.selectedJob.stage == HpcJobStageNames.STAGE_JOB_EXECUTE &&
        !this.selectedJob.status.includes(OperationStatus.Queued)
      ) {
        jobActions.push(
          new JobAction(JobActionsText.TxtOutput, JobActionsValue.ValOutput)
        );
        if (this.selectedJob?.isPlottable) {
          jobActions.push(
            new JobAction(JobActionsText.TxtGraph, JobActionsValue.ValGraph)
          );
        }
      }

      if (
        this.selectedJob.stage == HpcJobStageNames.STAGE_JOB_EXECUTE &&
        this.selectedJob.status == OperationStatus.Wait
      ) {
        jobActions.push(
          new JobAction(JobActionsText.TxtCancel, JobActionsValue.ValCancel)
        );
      }

      if (
        this.selectedJob.stage == HpcJobStageNames.STAGE_JOB_EXECUTE &&
        this.selectedJob.status == OperationStatus.Running &&
        this.selectedJob.application == JobTypes.AIML
      ) {
        jobActions.push(
          new JobAction(
            JobActionsText.TxtConnectToPod,
            JobActionsValue.ValConnectToPod
          )
        );
      }
    }
    this.CurrentCustomAction.forEach((c) => {
      jobActions.push(c);
    });
    return jobActions;
  }

  emitJobAction(action: JobAction) {
    let jobObj = {
      jobName: this.selectedJob.name,
      appName: this.selectedJob.application,
      appVersion: this.selectedJob.version,
      partition: this.selectedJob.partition,
      action: action,
    };
    this.jobService.updateJobAction(jobObj);
  }

  jobUpdated($event) { }
}
